import { useState } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";

// Compact, pre-filled quote form for the service detail sidebar.
export default function QuoteFormMini({ service }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and a short note are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        service: service.title,
      });
      toast.success("Inquiry received — we'll be in touch within one business day.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-forest p-8" data-testid="quote-form-success">
        <div className="uppercase text-[10px] tracking-[0.25em] text-gold mb-3">
          Inquiry received
        </div>
        <p className="font-serif text-2xl text-forest leading-snug">
          Thank you. A Greenroute specialist will be in touch shortly.
        </p>
        <a
          href="tel:+27661833893"
          className="mt-6 inline-flex items-center gap-2 text-sm text-graphite hover:text-forest"
        >
          <Phone size={14} /> Need it urgent? +27 66 183 3893
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-bordersoft p-6 md:p-8 space-y-4"
      data-testid="service-quote-form"
    >
      <div>
        <div className="uppercase text-[10px] tracking-[0.25em] text-gold mb-2">
          Quote request
        </div>
        <h3 className="font-serif text-2xl text-forest leading-tight">
          Get a quote for {service.title}
        </h3>
      </div>

      <input
        type="text"
        placeholder="Full name *"
        value={form.name}
        onChange={update("name")}
        data-testid="qf-name"
        className="w-full bg-cream border border-bordersoft px-4 py-3 text-ink text-sm focus:outline-none focus:border-forest"
      />
      <input
        type="email"
        placeholder="Email *"
        value={form.email}
        onChange={update("email")}
        data-testid="qf-email"
        className="w-full bg-cream border border-bordersoft px-4 py-3 text-ink text-sm focus:outline-none focus:border-forest"
      />
      <input
        type="tel"
        placeholder="Phone (optional)"
        value={form.phone}
        onChange={update("phone")}
        data-testid="qf-phone"
        className="w-full bg-cream border border-bordersoft px-4 py-3 text-ink text-sm focus:outline-none focus:border-forest"
      />
      <textarea
        rows={3}
        placeholder="Tell us briefly about your property & need *"
        value={form.message}
        onChange={update("message")}
        data-testid="qf-message"
        className="w-full bg-cream border border-bordersoft px-4 py-3 text-ink text-sm focus:outline-none focus:border-forest resize-none"
      />

      <button
        type="submit"
        disabled={submitting}
        data-testid="qf-submit"
        className="w-full inline-flex items-center justify-center gap-3 bg-forest text-cream px-6 py-3.5 text-sm tracking-wider hover:bg-forest/90 transition-colors duration-300 disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Request Quote"}
        <ArrowRight size={16} />
      </button>

      <div className="pt-3 border-t border-bordersoft text-center">
        <a
          href="tel:+27661833893"
          className="inline-flex items-center gap-2 text-xs text-graphite hover:text-forest"
          data-testid="qf-call-link"
        >
          <Phone size={12} /> Or call +27 66 183 3893
        </a>
      </div>
    </form>
  );
}
